import { Col, Row, Skeleton } from "antd";
import cloneDeep from "lodash/cloneDeep";
import { useCallback, useEffect, useState } from "react";
import { injectIntl, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import Page from "../../../Common/Page";
import PermissionCheck from "../../../Common/PermissionCheck";
import { PermissionNames, PermissionOperations } from "~enums/permissions";
import { useHttp } from "../../../hooks/useHttp";
import { columnsConfigBySportForDisplay, renameStandingPropertiesByConfig } from "../helpers";
import EditButton from "./EditButton";
import EditStandingsForm from "./EditStandingsForm";

function EditStandings() {
  const { formatMessage } = useIntl();

  const { sport, organizationId, standingId } = useParams();
  const {
    loading: fetchStandingsLoading,
    error: fetchStandingsError,
    request: fetchStandingsRequest,
    cleanError: fetchStandingsCleanError,
  } = useHttp();
  const {
    loading: fetchEntitiesLoading,
    error: fetchEntitiesError,
    request: fetchEntitiesRequest,
    cleanError: fetchEntitiesCleanError,
  } = useHttp();
  const [standings, setStandings] = useState(null);
  const [formStandings, setFormStandings] = useState(null);
  const [entityName, setEntityName] = useState(" ");
  const [entityList, setEntityList] = useState(null);

  const titleBase =
    `${entityName} - ` +
    formatMessage({
      id: "standings.row.edit",
      defaultValue: "Update Standings Row",
    });
  useEffect(() => {
    const fetchStandings = async () => {
      try {
        const res = await fetchStandingsRequest({
          url: `/v1/${sport}/o/${organizationId}/standings/${standingId}?include=entities`,
        });
        if (res?.data?.length) {
          const processedData = {
            ...res.data[0],
          };

          if (res?.data[0]?.calculated) {
            const deepCloneDefaultCalculatedSchema = cloneDeep(res.data[0].calculated);

            const calculatedSchemaWithConfigApplied = Object.keys(deepCloneDefaultCalculatedSchema).reduce(
              (acc, key) => {
                acc[key] = renameStandingPropertiesByConfig(
                  deepCloneDefaultCalculatedSchema[key],
                  columnsConfigBySportForDisplay[sport],
                );
                return acc;
              },
              {},
            );

            processedData.calculated = calculatedSchemaWithConfigApplied;
          }

          setStandings(processedData);
          setEntityName(res?.includes?.resources?.entities?.[res?.data[0].entity.id].nameFullLocal || "");
        }
      } catch (e) {
        console.error(e);
      }
    };

    fetchStandings();
  }, []);

  const fetchEntities = useCallback(async () => {
    try {
      const res = await fetchEntitiesRequest({
        url: `/v1/${sport}/o/${organizationId}/entities?limit=200`,
      });
      setEntityList(res?.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [fetchEntitiesRequest, sport, organizationId]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities, standingId]);

  const getStandingsFormData = useCallback((form) => {
    setFormStandings(form);
  }, []);

  const handleGetFormData = useCallback(async () => {
    const standingsFormDataSource = await formStandings.validateFields();
    return {
      ...standingsFormDataSource,
    };
  }, [formStandings]);

  if (fetchStandingsLoading || !standings) return <Skeleton />;

  return (
    <Page title={titleBase} tabTitle={titleBase}>
      <Row>
        <Col xs={{ span: 24, offset: 0 }} lg={{ span: 12, offset: 6 }}>
          {standings && (
            <EditStandingsForm
              standings={standings}
              getStandingsFormData={getStandingsFormData}
              entityList={entityList}
            />
          )}
          {standings && (
            <PermissionCheck
              permissionKeys={[PermissionNames.STANDING]}
              permissionOptions={[PermissionOperations.UPDATE]}
            >
              <EditButton handleGetFormData={handleGetFormData} />
            </PermissionCheck>
          )}
        </Col>
      </Row>
    </Page>
  );
}

export default EditStandings;
